export interface InfoSection {
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface InfoPageContent {
  title: string;
  /** Short lead paragraph shown under the title. */
  lead: string;
  /** "Last updated" date (legal/policy pages). */
  updated?: string;
  /** Whether to show the "working summary" notice (legal/policy stubs). */
  draft?: boolean;
  sections: InfoSection[];
  /** Contact email surfaced at the bottom, if any. */
  contactEmail?: string;
}

/**
 * Content for footer destinations. Legal/policy copy here is a plain-language
 * working summary for the beta — canonical text is owned by Legal
 * (see docs/05-legal-compliance and docs/06-trust-safety).
 */
export const INFO_CONTENT: Record<string, InfoPageContent> = {
  about: {
    title: 'About FetMode',
    lead: 'Your operating system for kink.',
    sections: [
      {
        paragraphs: [
          'FetMode unifies live streaming, video content, and community for consenting adults — one account, one wallet, one identity. Instead of forcing creators and members to hop between a cam site, a content platform, and a social app, we bring the whole experience together.',
        ],
      },
      {
        heading: 'Why we exist',
        paragraphs: [
          'The adult creator economy is fragmented and expensive. Live cams live on one platform, clips on another, community somewhere else — and most platforms take 40–50% while pushing creators to hustle across apps just to keep their audience.',
          'FetMode replaces that patchwork with a single ecosystem where members and creators actually own their relationships.',
        ],
      },
      {
        heading: 'What we stand for',
        bullets: [
          'Fair creator economics — up to 90% on offline tips and industry-leading payouts everywhere else.',
          'Responsible usage first — spend limits, session budgets, and self-exclusion built in, not bolted on.',
          'Consent and safety as non-negotiables, for members and creators alike.',
          'A community that treats kink with respect instead of stigma.',
          'Modern creator tools — FetMode Studio, voice control, and freemium editing for everyone.',
        ],
      },
      {
        heading: 'The ecosystem',
        bullets: [
          'fetmode.com — the consumer experience: Community, Live, and Videos.',
          'studio.fetmode.com — broadcasting and creator management.',
          'agency.fetmode.com — studios and agency roster tools.',
          'partners.fetmode.com — affiliate and referral tools.',
          'create.fetmode.com — creator publishing and editing tools.',
        ],
      },
    ],
    contactEmail: 'hello@fetmode.com',
  },
  careers: {
    title: 'Careers',
    lead: 'Help us build the operating system for kink.',
    sections: [
      {
        heading: 'How we work',
        paragraphs: [
          'We are a small, senior team working on genuinely hard problems across live video, payments, trust & safety, and community. We move quickly, take ownership end to end, and care deeply about doing adult technology responsibly.',
          'We believe adult platforms deserve the same engineering and design quality as any top consumer product — and that creators deserve to be treated as partners, not inventory.',
        ],
      },
      {
        heading: 'Where we need help',
        bullets: [
          'Engineering — real-time video, distributed systems, payments, and web/mobile.',
          'Trust & Safety — moderation operations, policy, and tooling.',
          'Payments & Risk — high-risk processing, fraud, and chargeback strategy.',
          'Creator Operations — onboarding, support, and success for models and studios.',
          'Design — product design and brand for a category that rarely gets great design.',
        ],
      },
      {
        heading: 'Get in touch',
        paragraphs: [
          'We are not always posting individual roles, but we are always interested in exceptional people. Send a short note about what you are great at and links to your work.',
        ],
      },
    ],
    contactEmail: 'careers@fetmode.com',
  },
  blog: {
    title: 'Blog',
    lead: 'Product updates, creator playbooks, and transparency.',
    sections: [
      {
        heading: 'What to expect',
        bullets: [
          'Product updates and feature launches.',
          'Creator playbooks — earning, streaming, and growing an audience.',
          'Safety and transparency notes, including our reporting practices.',
          'Community spotlights and event coverage.',
        ],
      },
      {
        heading: 'Coming soon',
        paragraphs: [
          'Our blog launches alongside the public beta. Follow us on social in the meantime for the earliest updates.',
        ],
      },
    ],
  },
  press: {
    title: 'Press',
    lead: 'Media and partnership inquiries.',
    sections: [
      {
        heading: 'Media inquiries',
        paragraphs: [
          'For press, brand, or partnership requests, reach our team directly. We respond to verified media inquiries as quickly as we can.',
        ],
      },
      {
        heading: 'Boilerplate',
        paragraphs: [
          'FetMode is an all-in-one platform for the adult creator economy — unifying live streaming, video content, and community for consenting adults, with fair creator payouts and responsible-usage tools built in from day one.',
        ],
      },
      {
        heading: 'Brand assets',
        paragraphs: [
          'Logos and brand guidelines are available on request. Please do not alter the FetMode logo, colors, or wordmark without approval.',
        ],
      },
    ],
    contactEmail: 'press@fetmode.com',
  },
  help: {
    title: 'Help center',
    lead: 'Answers to the most common questions.',
    sections: [
      {
        heading: 'Getting started',
        bullets: [
          'Create an account and confirm you are 18 or older (or the age of majority where you live).',
          'Choose how you want to use FetMode — Community, Live, or Videos — you can switch anytime.',
          'Complete age verification if your region requires it before accessing 18+ content.',
        ],
      },
      {
        heading: 'Coins & wallet',
        bullets: [
          'Coins are the single currency across tips, shows, videos, and wishlists.',
          'Buy any amount at or above the minimum — no fixed package traps.',
          'Set a session budget before a show and daily/weekly spend limits in settings.',
          'See your full purchase and spending history anytime in your wallet.',
        ],
      },
      {
        heading: 'Shows & privates',
        bullets: [
          'Main Stage shows are free to watch; tip to support and hit goals.',
          'VIP, Exclusive, Party, Ticket, Club, and Peep shows each have their own pricing and rules.',
          'Show boundaries and menus are visible before you join, so there are no surprises.',
        ],
      },
      {
        heading: 'Still need help?',
        paragraphs: [
          'If your question involves safety or a policy violation, use the in-product report tools. For account, wallet, verification, or playback issues, contact our support team.',
        ],
      },
    ],
    contactEmail: 'support@fetmode.com',
  },
  safety: {
    title: 'Safety & responsible use',
    lead: 'Tools and resources to keep FetMode healthy — no shame, just clear controls.',
    sections: [
      {
        heading: 'Responsible spending',
        bullets: [
          'Daily and weekly spend limits are on by default and fully adjustable in settings.',
          'Session budgets let you cap spending before you enter a show, with warnings at 50%, 75%, and 90%.',
          'Auto top-up includes a 15-minute cancel window, frequency caps, and a daily hard cap.',
          'Your limits are private — creators never see them.',
        ],
      },
      {
        heading: 'Take a break',
        bullets: [
          'Self-exclude for anywhere from 24 hours to 6 months.',
          'Reality checks remind you how long you have been active.',
          'Anonymous spending insights help you notice patterns early.',
        ],
      },
      {
        heading: 'Blocking & reporting',
        paragraphs: [
          'Block anyone, report content or behavior from any profile, message, or stream, and hide your activity instantly. Reporter identity is never shared with the person you report.',
        ],
      },
      {
        heading: 'Get support',
        paragraphs: [
          'We provide one-tap access to region-specific crisis helplines, referrals to sex-work-friendly therapists, and financial counseling for anyone worried about their spending. Reaching out is a strength, not a failure.',
        ],
      },
    ],
    contactEmail: 'safety@fetmode.com',
  },
  feedback: {
    title: 'Send feedback',
    lead: 'We build FetMode with our community.',
    sections: [
      {
        heading: 'What we want to hear',
        bullets: [
          'Features you wish existed.',
          'Things that feel confusing or slow.',
          'What is working well and worth keeping.',
        ],
      },
      {
        heading: 'Report a bug',
        paragraphs: [
          'Found something broken? Tell us what you were doing, what you expected, and what happened instead. Screenshots help a lot.',
        ],
      },
    ],
    contactEmail: 'feedback@fetmode.com',
  },
  terms: {
    title: 'Terms of Service',
    lead: 'The agreement between you and FetMode.',
    updated: 'Beta',
    draft: true,
    sections: [
      {
        heading: '1. Eligibility',
        paragraphs: [
          'You must be at least 18 years old, or the age of majority in your jurisdiction, and legally able to enter into this agreement. Where the law requires it, you must complete age or identity verification before accessing adult content.',
        ],
      },
      {
        heading: '2. Your account',
        paragraphs: [
          'You are responsible for your account and for keeping your credentials secure. You may not share, sell, or transfer your account, and you may not create accounts to evade restrictions.',
        ],
      },
      {
        heading: '3. Coins & payments',
        bullets: [
          'Coins are a prepaid, limited license to access features and content — they are not legal tender and, except where explicitly offered, are non-refundable and non-transferable.',
          'You may purchase any amount at or above the platform minimum. Optional auto top-up can be cancelled within its cancellation window.',
          'Chargebacks and payment fraud may result in account restrictions under our responsible-usage enforcement.',
          'Trusted members may be offered a post-pay tab; abuse of it can result in revocation and restrictions.',
        ],
      },
      {
        heading: '4. Creator payouts',
        paragraphs: [
          'Creators earn a published share of eligible activity — including higher rates on offline tips and fan-club subscriptions. Payout eligibility requires identity verification and compliance with these terms and our guidelines.',
        ],
      },
      {
        heading: '5. Content & licensing',
        paragraphs: [
          'You retain ownership of content you create. You grant FetMode a limited license to host, display, and distribute it solely to operate the service. You are responsible for having all rights and consents for anything you upload or stream.',
        ],
      },
      {
        heading: '6. Prohibited conduct',
        paragraphs: [
          'You must follow our Community Guidelines. Content involving minors, non-consensual acts, coercion, doxxing, harassment, impersonation, or illegal activity is strictly forbidden and may be reported to authorities.',
        ],
      },
      {
        heading: '7. Adult content acknowledgment',
        paragraphs: [
          'FetMode contains sexually explicit material intended only for adults. By using the service you acknowledge you wish to access such material and that it is lawful to do so where you are.',
        ],
      },
      {
        heading: '8. Termination',
        paragraphs: [
          'You may close your account at any time. We may suspend or terminate accounts that violate these terms or our guidelines, or where required by law.',
        ],
      },
      {
        heading: '9. Disclaimers & liability',
        paragraphs: [
          'The service is provided "as is." To the maximum extent permitted by law, FetMode is not liable for indirect or consequential damages arising from your use of the platform.',
        ],
      },
      {
        heading: '10. Disputes & changes',
        paragraphs: [
          'Dispute resolution and governing law will be specified for your jurisdiction. We will give notice of material changes to these terms; continued use after changes means you accept them.',
        ],
      },
    ],
    contactEmail: 'legal@fetmode.com',
  },
  privacy: {
    title: 'Privacy Policy',
    lead: 'How we collect, use, and protect your data.',
    updated: 'Beta',
    draft: true,
    sections: [
      {
        heading: 'What we collect',
        bullets: [
          'Account details you provide (email, username, profile information).',
          'Age and identity verification status (see below).',
          'Payment and transaction records needed to process purchases and payouts.',
          'Usage and device data to operate, secure, and improve the service.',
          'Approximate location (from your connection) to apply regional rules.',
        ],
      },
      {
        heading: 'Age & identity verification',
        paragraphs: [
          'Where the law requires it, verification is handled by third-party vendors. We aim to store a verification status and a vendor reference — not copies of your ID — and we minimize the personal data we retain.',
        ],
      },
      {
        heading: 'How we use your data',
        bullets: [
          'To provide the service, process payments, and pay creators.',
          'To keep the community safe and enforce our guidelines.',
          'To comply with legal obligations, including age and records requirements.',
          'With your consent, for analytics and personalization.',
        ],
      },
      {
        heading: 'Sharing',
        paragraphs: [
          'We do not sell your personal information. We share data only with service providers (such as payment and verification vendors) under contract, or when required by law.',
        ],
      },
      {
        heading: 'Your rights',
        paragraphs: [
          'Depending on where you live, you may request access to, correction of, export of, or deletion of your personal data. Contact us to exercise these rights.',
        ],
      },
      {
        heading: 'Retention & security',
        paragraphs: [
          'We keep data only as long as needed to operate the service and meet legal requirements, and we protect it with encryption and access controls.',
        ],
      },
    ],
    contactEmail: 'privacy@fetmode.com',
  },
  cookies: {
    title: 'Cookie Policy',
    lead: 'How we use cookies and similar technologies.',
    updated: 'Beta',
    draft: true,
    sections: [
      {
        heading: 'What cookies are',
        paragraphs: [
          'Cookies are small files stored on your device that help websites work and remember your preferences.',
        ],
      },
      {
        heading: 'Types we use',
        bullets: [
          'Essential — required to run the site, keep you signed in, and protect against fraud. These are always on.',
          'Analytics — help us understand usage so we can improve. These load only after you accept.',
          'Preferences — remember choices like theme and language.',
        ],
      },
      {
        heading: 'Managing your choices',
        paragraphs: [
          'You can accept or reject non-essential cookies at any time using the "Cookie preferences" link in the footer. Non-essential cookies never load until you opt in.',
        ],
      },
    ],
    contactEmail: 'privacy@fetmode.com',
  },
  '2257': {
    title: '18 U.S.C. § 2257 Compliance Statement',
    lead: 'Records-keeping compliance for adult content.',
    updated: 'Beta',
    draft: true,
    sections: [
      {
        heading: 'Statement',
        paragraphs: [
          'All persons appearing in any visual depiction of actual or simulated sexually explicit conduct on FetMode were over the age of 18 at the time the content was created.',
        ],
      },
      {
        heading: 'Records',
        paragraphs: [
          'Records required by 18 U.S.C. § 2257 and its regulations are maintained by the respective content producers. FetMode requires verified age documentation from every creator before any adult content may be published or streamed.',
        ],
      },
      {
        heading: 'User-generated content',
        paragraphs: [
          'For content produced by creators on the platform, the individual creator is the primary records custodian. FetMode maintains verification records for creators as part of onboarding and payout eligibility.',
        ],
      },
    ],
    contactEmail: 'compliance@fetmode.com',
  },
  dmca: {
    title: 'DMCA / Copyright',
    lead: 'How to report copyright infringement.',
    updated: 'Beta',
    draft: true,
    sections: [
      {
        paragraphs: [
          'FetMode respects intellectual property rights and responds to valid takedown notices under the Digital Millennium Copyright Act.',
        ],
      },
      {
        heading: 'Filing a notice',
        bullets: [
          'Identify the copyrighted work you claim has been infringed.',
          'Provide the URL(s) of the infringing material on FetMode.',
          'Include your contact information (name, email, address).',
          'State, in good faith, that the use is not authorized by the owner, its agent, or the law.',
          'Confirm the information is accurate and that you are authorized to act, under penalty of perjury.',
          'Include your physical or electronic signature.',
        ],
      },
      {
        heading: 'Counter-notice & repeat infringers',
        paragraphs: [
          'If your content was removed in error, you may submit a counter-notice. We terminate the accounts of repeat infringers.',
        ],
      },
    ],
    contactEmail: 'dmca@fetmode.com',
  },
  'content-removal': {
    title: 'Content removal',
    lead: 'Request removal of content that involves you.',
    updated: 'Beta',
    draft: true,
    sections: [
      {
        heading: 'Who can request removal',
        paragraphs: [
          'If you appear in content on FetMode and want it removed, or you believe content was published without your consent, contact us immediately. Reports of non-consensual content are our highest priority.',
        ],
      },
      {
        heading: 'How to request',
        bullets: [
          'Tell us the URL(s) of the content.',
          'Explain your relationship to the content and why it should be removed.',
          'Provide a way for us to contact you.',
        ],
      },
      {
        heading: 'What happens next',
        paragraphs: [
          'We remove verified non-consensual or illegal content promptly, restrict the responsible accounts, and cooperate with law enforcement where required.',
        ],
      },
    ],
    contactEmail: 'abuse@fetmode.com',
  },
  guidelines: {
    title: 'Community Guidelines',
    lead: 'What keeps FetMode a place worth being.',
    updated: 'Beta',
    draft: true,
    sections: [
      {
        paragraphs: [
          'FetMode is for consenting adults. These guidelines keep everyone safer. Violations can lead to warnings, feature restrictions, or a permanent ban.',
        ],
      },
      {
        heading: 'Core rules',
        bullets: [
          '18+ only — no minors, no content involving minors, no "barely legal" themes.',
          'Consent always — no pressure, coercion, or ignoring stated boundaries.',
          'No non-consensual content — including leaked or revenge imagery.',
          'Respect privacy — no outing, doxxing, or sharing others\u2019 information.',
          'No harassment — including hate speech, stalking, brigading, or unwanted explicit messages.',
          'Be honest — no impersonation, catfishing, or scams.',
          'Legal activity only — do not use FetMode for illegal acts.',
        ],
      },
      {
        heading: "What's allowed",
        bullets: [
          'Consensual adult expression and imagery between adults.',
          'Open negotiation of interests and boundaries.',
          'Event promotion that follows event rules.',
        ],
      },
      {
        heading: 'Reporting & appeals',
        paragraphs: [
          'Report anything that crosses a line using the in-product tools; your identity is not shared with the reported party. If we act on your account, you can appeal — a different reviewer will look at it, and we aim to respond within a few business days.',
        ],
      },
    ],
    contactEmail: 'trust@fetmode.com',
  },
};
